import {
  NumberInput,
  PasswordInput,
  SubmitButton,
  SwitchInput,
  TextInput,
} from "@ameelio/ui";
import { useMutation } from "@apollo/client/react";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CopyToClipboard from "../lib/CopyToClipboard";
import useApolloErrorHandler from "../lib/handleApolloError";
import Screen from "../lib/Screen";
import {
  hasMaxValue,
  hasMinValue,
  isPassword,
  isRequired,
  mergeRules,
} from "../lib/validate";
import { CreateSystemDocument } from "./CreateSystem.generated";

export const DEFAULT_VISITOR_REAPPROVAL_DAYS = 7;

type FormData = {
  name: string;
  code: string;
  dataRetention: number;
  recordingRetention: number;
  visitorReapprovalDays: number;
  maxPasswordAgeDays: number | null;
  ameelioStaffPassword: string;
  hasCallTranscripts: boolean;
  hasTablets: boolean;
  allowInmatePasswordReset: boolean;
  enableCovertMessageFlagging: boolean;
};

export default function CreateSystemScreen() {
  const navigate = useNavigate();
  const handleApolloError = useApolloErrorHandler();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({
    mode: "onTouched",
    defaultValues: {
      visitorReapprovalDays: DEFAULT_VISITOR_REAPPROVAL_DAYS,
      maxPasswordAgeDays: null,
      hasCallTranscripts: true,
      hasTablets: false,
      allowInmatePasswordReset: false,
      enableCovertMessageFlagging: true,
      dataRetention: undefined,
    },
  });

  const [createSystem] = useMutation(CreateSystemDocument, {
    onError: (e) => handleApolloError(e),
  });

  const onSubmit = async ({ code, ...input }: FormData) => {
    await createSystem({
      variables: {
        input: {
          ...input,
          code: code.toUpperCase(),
          dataRetention: input.dataRetention ?? undefined,
          visitorReapprovalDays: input.visitorReapprovalDays ?? undefined,
        },
      },
      onCompleted: (data) =>
        navigate(`/toolbox/systems/${data.createSystem.system.id}`),
    });
  };

  const code = watch("code");
  const staffAccountEmail = code
    ? `connect-support+${code.toLowerCase()}@ameelio.org`
    : "connect-support+CODE@ameelio.org";

  return (
    <Screen title="Create system">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <TextInput
            control={control}
            name="name"
            label={t("Name")}
            rules={isRequired("Please provide a name")}
          />
          <TextInput
            control={control}
            name="code"
            label="Code"
            helperText={`The resident portal URL will be: https://${
              code?.toLowerCase() || "code"
            }-app.ameelio.org`}
            rules={mergeRules(isRequired("Please provide a valid code."), {
              pattern: /^[A-Za-z-]{1,63}$/,
            })}
          />
          <NumberInput
            control={control}
            name="recordingRetention"
            label="Recording retention (days)"
            helperText="The number of days we will keep recordings and transcripts."
            rules={mergeRules(
              isRequired("Recording retention is required"),
              hasMinValue(0),
              hasMaxValue(731)
            )}
          />
          <NumberInput
            control={control}
            name="dataRetention"
            helperText="The number of days we will keep meeting logs and inactive accounts. Leave blank to retain indefinitely."
            label="Data retention (days)"
            rules={mergeRules(hasMinValue(0), hasMaxValue(5000))}
          />
          <NumberInput
            control={control}
            name="visitorReapprovalDays"
            label="Visitor re-approval window (days)"
            helperText="The number of days since a resident's profile has become inactive after which their previously approved visitors will need to re-request approval."
            rules={mergeRules(
              isRequired("Visitor re-approval window is required"),
              hasMinValue(0)
            )}
          />
          <NumberInput
            control={control}
            name="maxPasswordAgeDays"
            label="Visitor max password age (days)"
            helperText="Visitors with an organization membership in this system must reset their password if it is older than this many days. Leave empty for no limit."
            rules={hasMinValue(1)}
          />
          <SwitchInput
            control={control}
            name="hasCallTranscripts"
            label="Enable call transcripts?"
          />
          <SwitchInput
            control={control}
            name="hasTablets"
            label="Enable tablet management?"
          />
          <SwitchInput
            control={control}
            name="allowInmatePasswordReset"
            label="Enable resident password reset flow?"
          />
          <SwitchInput
            control={control}
            name="enableCovertMessageFlagging"
            label="Enable covert message flagging?"
          />
          <Stack spacing={2} component="fieldset">
            <legend>Ameelio staff account</legend>
            <Typography variant="body1">
              Create a NordPass entry with a generated password, then share it
              with support.
            </Typography>
            <CopyToClipboard label="Username" value={staffAccountEmail} />
            <PasswordInput
              control={control}
              name="ameelioStaffPassword"
              label="Password"
              rules={isPassword(10)}
            />
          </Stack>
          <SubmitButton submitting={isSubmitting}>{t("Submit")}</SubmitButton>
        </Stack>
      </form>
    </Screen>
  );
}
